import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { Loading, ScalrEmptyBox } from '@/components/customComponents'
import { ScalrBoxAccordionView } from '@/components/customComponents/scalrBox'

import {
  useCategoryAlerts,
  useSpendLensCategories,
  useSpendLensItems,
  useSpendLensSubCategories,
} from '@/hooks'
import {
  pushNotification,
  useAppDispatch,
  usePageRefreshTrigger,
  useReduxBaseFilter,
} from '@/store'
import { IAbstractLensItem } from '@/types'
import { dateToString } from '@/utils'

const SpendLensPage = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  const { selectedBranch, date, dateFilterType } =
    useReduxBaseFilter('spend-lens')
  const refreshTrigger = usePageRefreshTrigger('spend-lens')
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [selectedSubCategory, setSelectedSubCategory] = useState<string | null>(
    null
  )

  const { getCategoryAlertCount, getItemAlertCount } =
    useCategoryAlerts('Spend Lens')

  const {
    formattedCategoriesData,
    loading: categoriesLoading,
    refetch: categoriesRefetch,
  } = useSpendLensCategories(
    dateToString(date, 'from'),
    dateToString(date, 'to'),
    dateFilterType,
    selectedBranch
  )

  const {
    fetchSubCategoriesForCategory,
    hasSubCategoriesForCategory,
    getFormattedSubCategoriesForCategory,
    loading: subCategoriesLoading,
    refetch: subCategoriesRefetch,
  } = useSpendLensSubCategories(
    dateToString(date, 'from'),
    dateToString(date, 'to'),
    dateFilterType,
    selectedBranch
  )

  const {
    fetchItemsForCategory,
    hasItemsForCategory,
    getFormattedItemsForCategory,
    loading: itemsLoading,
    refetch: itemsRefetch,
  } = useSpendLensItems(
    dateToString(date, 'from'),
    dateToString(date, 'to'),
    dateFilterType,
    selectedBranch
  )

  // Watch for refresh trigger from Redux
  useEffect(() => {
    if (refreshTrigger) {
      console.log('Refreshing all spend lens data...')
      setSelectedCategory(null)
      setSelectedSubCategory(null)
      categoriesRefetch()
      subCategoriesRefetch()
      itemsRefetch()
    }
  }, [refreshTrigger, categoriesRefetch, subCategoriesRefetch, itemsRefetch])

  useEffect(() => {
    setSelectedCategory(null)
    setSelectedSubCategory(null)
  }, [selectedBranch, date])

  const displayData = useMemo(() => {
    const result: Array<IAbstractLensItem<any>> = []

    formattedCategoriesData.forEach((category) => {
      // Add category (Level 1)
      result.push({ ...category })

      if (selectedCategory === category.item) {
        const formattedSubCategories = getFormattedSubCategoriesForCategory(
          category.item
        )
        // SubCategories now have parentName set from model's parentCategory field
        result.push(...formattedSubCategories)
      }
    })

    return result
  }, [
    formattedCategoriesData,
    selectedCategory,
    getFormattedSubCategoriesForCategory,
  ])

  const expandedSubCategoryItems = useMemo(() => {
    const result: Record<string, IAbstractLensItem<any>[]> = {}

    // Build expanded items for all subcategories that have been fetched
    formattedCategoriesData.forEach((category) => {
      if (selectedCategory === category.item) {
        const formattedSubCategories = getFormattedSubCategoriesForCategory(
          category.item
        )

        // For each subcategory, check if items have been fetched
        formattedSubCategories.forEach((subCategory) => {
          const subCategoryName = subCategory.item
          if (subCategoryName && hasItemsForCategory(subCategoryName)) {
            result[subCategoryName] =
              getFormattedItemsForCategory(subCategoryName)
          }
        })
      }
    })

    return result
  }, [
    formattedCategoriesData,
    selectedCategory,
    getFormattedSubCategoriesForCategory,
    hasItemsForCategory,
    getFormattedItemsForCategory,
  ])

  const handleCategoryClick = useCallback(
    async (category: IAbstractLensItem<any>) => {
      const categoryName = category.item
      if (!categoryName) return

      // If the item has a parentName, it's an item (child), not a category
      const isActualItem = !!category.parentName

      const isParentCategory =
        formattedCategoriesData.some((cat) => cat.item === categoryName) &&
        !isActualItem

      if (!isParentCategory) {
        return
      }

      if (
        selectedCategory === categoryName &&
        hasSubCategoriesForCategory(categoryName)
      ) {
        setSelectedCategory(null)
        return
      }

      try {
        await fetchSubCategoriesForCategory(categoryName)
        setSelectedCategory(categoryName)
      } catch (err) {
        const error = new Error(
          err instanceof Error ? err.message : 'An unknown error occurred'
        )

        dispatch(
          pushNotification({
            type: 'error',
            title: t('common.errors.serverError'),
            description: error.message,
            duration: 5000,
          })
        )
      }
    },
    [
      selectedCategory,
      formattedCategoriesData,
      fetchSubCategoriesForCategory,
      hasSubCategoriesForCategory,
    ]
  )

  const handleSubCategoryClick = useCallback(
    async (subCategory: IAbstractLensItem<any>) => {
      const subCategoryName = subCategory.item
      if (!subCategoryName) return

      // If the item has a parentName, it's a child (Level 2 - subcategory)
      const isSubCategory = !!subCategory.parentName

      // Only handle subcategories (Level 2), not categories (Level 1)
      if (!isSubCategory) {
        setSelectedSubCategory(null)
        return
      }

      if (
        selectedSubCategory === subCategoryName &&
        hasItemsForCategory(subCategoryName)
      ) {
        setSelectedSubCategory(null)
        return
      }

      try {
        // Fetch items (Level 3 - SKUs) for this subcategory
        // The items will be stored in the hook's cache and retrieved via expandedSubCategoryItems useMemo
        await fetchItemsForCategory(subCategoryName)
        setSelectedSubCategory(subCategoryName)
      } catch (err) {
        const error = new Error(
          err instanceof Error ? err.message : 'An unknown error occurred'
        )

        dispatch(
          pushNotification({
            type: 'error',
            title: t('common.errors.serverError'),
            description: error.message,
            duration: 5000,
          })
        )
      }
    },
    [fetchItemsForCategory]
  )

  const handleItemClick = useCallback(
    (item: IAbstractLensItem<any>, skuItem?: string) => {
      if (!skuItem) return

      const categoryName = item.parentName || ''
      const subcategoryName = item.item || ''

      // Store context and navigate to detail page
      localStorage.setItem('categoryName', categoryName)
      localStorage.setItem('subCategoryName', subcategoryName)
      localStorage.setItem('itemName', skuItem)

      navigate(`/clarity-lens/spend-lens/${skuItem}`)
    },
    [navigate]
  )

  return (
    <div className="space-y-4">
      {categoriesLoading && displayData.length === 0 ? (
        <Loading isContainer />
      ) : displayData.length === 0 ? (
        <ScalrEmptyBox />
      ) : (
        <ScalrBoxAccordionView
          data={displayData}
          expandedItems={expandedSubCategoryItems}
          onCategoryClick={handleCategoryClick}
          onSubCategoryClick={handleSubCategoryClick}
          onItemClick={handleItemClick}
          selectedItem={selectedCategory}
          loading={categoriesLoading || subCategoriesLoading || itemsLoading}
          getCategoryAlertCount={getCategoryAlertCount}
          getSubItemAlertCount={getItemAlertCount}
          isThreeLevel={true}
        />
      )}
    </div>
  )
}

export default SpendLensPage
